import { useEffect, useRef, type ReactNode } from "react";
import { DownOutlined, FileTextOutlined, HistoryOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { GuestHistory } from "../../../../api/reservations";
import { EM_DASH } from "../../../../components/dataTable";
import { fmtEuro } from "../../../../utils/currency";
import { formatCalendarDate, hasMissedReservations } from "../../logic/guestHistory";
import { useReservationFormat } from "../../hooks/useReservationFormat";

// Познатият телефон в „Нова резервация": чипът в полето „Телефон" и
// картата с историята под него (отваря се с чипа — не заема място постоянно).

const GUEST_HISTORY_CARD_ID = "reservation-guest-history";

const capitalizeFirst = (text: string) => text.charAt(0).toLocaleUpperCase() + text.slice(1);

/**
 * „◷ 5 резервации ⌄" вдясно в полето „Телефон" — номерът е познат.
 * На тесен екран — само „◷ 5 ⌄": номерът в международен запис с интервали
 * („+359 899 000 727") трябва да се побере; целият текст е в `aria-label` и в
 * картата. Мястото за чипа в полето — `guestHistoryChipRoom` (formStyles).
 * Червената точка: има отказани или неизпълнени. Кликът отваря/скрива картата.
 */
export const GuestHistoryChip = ({
  history,
  open,
  isNarrow,
  onToggle,
}: {
  history: GuestHistory;
  open: boolean;
  isNarrow: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTranslation();
  const hasMisses = hasMissedReservations(history);
  const countLabel = t("reservations.form.guestHistory.reservations", { count: history.reservations });
  const ariaLabel = [t("reservations.form.guestHistory.title"), countLabel]
    .concat(hasMisses ? [t("reservations.form.guestHistory.misses")] : [])
    .join(" · ");
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={GUEST_HISTORY_CARD_ID}
      aria-label={ariaLabel}
      title={t(open ? "reservations.form.guestHistory.hide" : "reservations.form.guestHistory.show")}
      className={`absolute right-1.5 top-1/2 flex -translate-y-1/2 cursor-pointer items-center gap-1.5 rounded-[8px] border-none pl-2 pr-1.5 text-[12px] font-bold leading-[1.2] text-brand-deep-ink outline-none transition-colors duration-150 hover:bg-brand-tint2 focus-visible:shadow-[0_0_0_2px_var(--color-brand)] ${
        isNarrow ? "h-9" : "h-8"
      } ${open ? "bg-brand-tint2" : "bg-brand-tint"}`}
    >
      <HistoryOutlined aria-hidden className="text-[12px]" />
      <span className="whitespace-nowrap tabular-nums">
        {isNarrow ? history.reservations : countLabel}
      </span>
      {hasMisses && <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-danger" />}
      <DownOutlined
        aria-hidden
        className={`text-[9px] transition-transform duration-150 ${open ? "rotate-180" : ""}`}
      />
    </button>
  );
};

/** Брояч вдясно на името: „2 резервации"; `tone` — червен (неизпълнени) / в акцента (настанени сега). */
const Count = ({ text, tone = "plain" }: { text: string; tone?: "plain" | "danger" | "brand" }) => (
  <span
    className={`whitespace-nowrap ${
      tone === "danger" ? "font-semibold text-danger-ink" : tone === "brand" ? "font-semibold text-brand-deep-ink" : ""
    }`}
  >
    {text}
  </span>
);

/** Ред „етикет — стойност" (последно идване, средна сметка). */
const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <>
    <dt className="text-muted">{label}</dt>
    <dd className="m-0 min-w-0 font-semibold text-ink">{children}</dd>
  </>
);

/**
 * Картата „История" — по образеца на потребителя: името от последната
 * резервация, вдясно броячите (резервации, посещения, предстоящи, отказани;
 * неизпълнените и настанените сега — само ако ги има), под тях последното
 * идване и средната сметка, най-долу последната бележка с датата ѝ.
 */
export const GuestHistoryCard = ({ history }: { history: GuestHistory }) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  // „ГОСТ" е последната секция на формата — отворена при долния край,
  // картата би останала под него; при отваряне се показва цялата
  // (`scroll-mb-28` — над лепкавия футър с бутоните на телефон: 69 px
  // футър + 24 px отстъп на панела + половината safe-area на iPhone).
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    cardRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, []);
  // Дни назад — точността е ден, затова „сега" при рисуването стига.
  const nowMs = Date.now();
  const historyText = (key: string, options: { count?: number; date?: string } = {}) =>
    t(`reservations.form.guestHistory.${key}`, options);

  return (
    <div
      ref={cardRef}
      id={GUEST_HISTORY_CARD_ID}
      className="flex scroll-mb-28 flex-col gap-3 rounded-[12px] bg-panel px-3.5 py-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 line-clamp-2 break-words text-[14px] font-bold leading-[1.3] tracking-[-0.2px] text-ink">
          {history.guestName}
        </div>
        <div className="grid shrink-0 grid-cols-[auto_auto] gap-x-3 gap-y-0.5 text-[11.5px] font-medium leading-[1.4] tabular-nums text-body">
          <Count text={historyText("reservations", { count: history.reservations })} />
          <Count text={historyText("visits", { count: history.visits })} />
          <Count text={historyText("upcoming", { count: history.upcoming })} />
          <Count text={historyText("cancelled", { count: history.cancelled })} />
          {history.expired > 0 && <Count tone="danger" text={historyText("expired", { count: history.expired })} />}
          {history.seated > 0 && <Count tone="brand" text={historyText("seated", { count: history.seated })} />}
        </div>
      </div>

      <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-[12.5px] leading-[1.4]">
        <Fact label={historyText("lastVisit")}>
          {history.lastVisitAt ? (
            <>
              {capitalizeFirst(format.daysAgo(history.lastVisitAt, nowMs))}
              <span className="font-medium text-muted tabular-nums"> · {formatCalendarDate(history.lastVisitAt)}</span>
            </>
          ) : (
            EM_DASH
          )}
        </Fact>
        <Fact label={historyText("averageBill")}>
          <span className="tabular-nums">{history.averageBill !== null ? fmtEuro(history.averageBill) : EM_DASH}</span>
        </Fact>
      </dl>

      {history.lastNote && (
        <div className="flex items-start gap-2 border-t border-shade-2 pt-2.5 text-[12.5px] leading-[1.45]">
          <FileTextOutlined aria-label={historyText("lastNote")} className="mt-[3px] shrink-0 text-muted" />
          <span className="min-w-0 flex-1 whitespace-pre-line break-words text-ink-2">{history.lastNote}</span>
          {history.lastNoteAt && (
            <span className="shrink-0 text-[11.5px] font-medium text-muted tabular-nums">
              {formatCalendarDate(history.lastNoteAt)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
